import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { AttentionIcon, Button, Refresh2Icon } from '@blade/ui-kit'
import { InfoCircleIcon, type InfoIntent } from '@root/shared/ui-kit'

import { ExploreTableSkeleton } from './explore-table-skeleton'

type ExploreTableEmptyProps = {
  onRetry: () => void
  intent: InfoIntent
}

export const ExploreTableEmpty: React.FC<ExploreTableEmptyProps> = ({
  onRetry,
  intent,
}) => {
  const { t } = useTranslation()
  const retryText = intent === 'error' ? t('common.retry') : t('common.refresh')

  const Icon = intent === 'error' ? AttentionIcon : InfoCircleIcon
  const title =
    intent === 'error' ? 'common.loading-error' : 'common.empty-unfortunately'
  const description =
    intent === 'error' ? 'common.something-went-wrong' : 'common.no-items-found'

  return (
    <Root>
      <Message>
        <IconWrapper>
          <Icon size={24} />
        </IconWrapper>
        <Title>{t(title)}</Title>
        <Description>{t(description)}</Description>
        <ButtonAlt
          variant="soft"
          size="xxs"
          radius="rounded"
          iconLeft={<Refresh2Icon size={12} />}
          onClick={onRetry}
        >
          {retryText}
        </ButtonAlt>
      </Message>
      <Background>
        {Array.from({ length: 10 }).map((_, index) => (
          <ExploreTableSkeleton key={index} disabled />
        ))}
      </Background>
    </Root>
  )
}
const Root = styled.div`
  display: grid;
  position: relative;
  height: 100%;

  &::after {
    content: '';

    background: linear-gradient(0deg, var(--appearanceA-9) 0%, transparent 80%);

    position: absolute;
    z-index: 1;
    bottom: 0;
    left: 0;
    top: 0;
    width: 100%;
    z-index: -1;
  }
`

const Background = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;

  display: grid;
  padding: 10px 0;
  z-index: -1;
`

const Message = styled.div`
  color: var(--textA-10);
  display: grid;

  justify-items: center;
  min-width: 230px;

  place-content: center;
  z-index: 1;
`

const IconWrapper = styled.div`
  height: 26px;
`

const Title = styled.div`
  color: var(--text-10);
  font-weight: 600;
  font-size: 14px;

  text-align: center;
  line-height: 20px;
  margin-top: 4px;
`

const Description = styled.p`
  color: var(--text-8);
  text-align: center;

  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
`

const ButtonAlt = styled(Button)`
  --button-radius-rounded: 6px;
  color: var(--text-12);

  padding-inline: 8px;
  border-radius: 6px;
  font-weight: 500;
  font-size: 10px;

  margin-top: 14px;
`
